/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import initBem from 'js/lib/bem';

import type { Theme } from '@coursera/cds-core';

import GradeNotification from 'bundles/compound-assessments/components/form-parts/GradeNotification';
import ValidationError from 'bundles/compound-assessments/components/form-parts/ValidationError';
import Option from 'bundles/compound-assessments/components/form-parts/checkbox/Option';
import type { FormPartsValidationStatus } from 'bundles/compound-assessments/components/form-parts/lib/checkResponsesInvalid';
import { FormPartsValidationStatuses } from 'bundles/compound-assessments/components/form-parts/lib/constants';
import { typeNames } from 'bundles/compound-assessments/constants';
import type {
  McqPrompt,
  McqReflectPrompt,
  McqReflectResponse,
  McqResponse,
} from 'bundles/compound-assessments/types/FormParts';

type Prompt = McqPrompt | McqReflectPrompt;
type Response = McqResponse | McqReflectResponse;

type Props = {
  prompt?: Prompt;
  response?: Response;
  onChangeResponse: (response: Response) => void;
  isDisabled: boolean;
  isReadOnly: boolean;
  showValidation: boolean;
  role?: string;
  ariaLabelledBy?: string;
  ariaDisabled?: boolean;
};
const bem = initBem('FormPartsMcq');

export const checkInvalid = (response?: Response): FormPartsValidationStatus | null =>
  !(((response || {}).definition || {}).value || {}).chosen ? FormPartsValidationStatuses.warning : null;

const styles = {
  validationError: css({
    transform: 'translate(-10px)',
  }),
  optionContainer: (theme: Theme) =>
    css({
      ':not(:first-child)': {
        marginTop: 'var(--cds-spacing-150)',
      },
    }),
};

/**
 * Mcq - renders a group of radio buttons
 * Known accessibility issues
 * @link https://blog.tenon.io/accessible-validation-of-checkbox-and-radiobutton-groups
 */
class Mcq extends React.Component<Props> {
  chose = (chosen: string) => {
    const { onChangeResponse } = this.props;
    onChangeResponse({
      typeName: typeNames.AUTO_GRADABLE_RESPONSE,
      definition: {
        value: {
          chosen,
        },
      },
    });
  };

  render() {
    const { prompt, response, isDisabled, isReadOnly, showValidation, role, ariaLabelledBy, ariaDisabled } = this.props;

    if (prompt) {
      const { chosen } = ((response || {}).definition || {}).value || {};
      const { options } = prompt.variant.definition;

      const isInvalid = !!(showValidation && checkInvalid(response));
      return (
        <div className={bem()} role={role ?? 'group'} aria-labelledby={ariaLabelledBy}>
          {options &&
            options.map((option, index) => (
              <div key={option.id} css={styles.optionContainer}>
                <Option
                  onChange={() => this.chose(option.id)}
                  option={option}
                  isSelected={chosen === option.id}
                  isDisabled={isDisabled}
                  isReadOnly={isReadOnly}
                  isRadio={true}
                  promptId={prompt.id}
                  inputHtmlAttributes={{
                    'aria-describedby': isInvalid && index === 0 ? `validation-error-${prompt.id}` : undefined, // work around to force screen reader to read validation message
                    'aria-disabled': ariaDisabled,
                  }}
                />
              </div>
            ))}
          <GradeNotification prompt={prompt} />
          {isInvalid && (
            <div css={styles.validationError}>
              <ValidationError id={prompt.id} />
            </div>
          )}
        </div>
      );
    }
    return null;
  }
}

export default Mcq;
